/* eslint-disable no-prototype-builtins */
import {UserModel} from 'app/modules/auth'
// Only used by the hidden "Complete your onboarding" widget (see below):
// import ClientOnboardPage from 'app/pages/school/ClientOnboard/ClientOnboardPage'
import {
  getActiveSession,
  // hasPermission, isAdmin, isLocalHost — only used by the hidden onboarding blocks (see below)
  isSelectiveAiDomains,
} from 'common/HelperFunctions'
import {USER_TYPES_NUMERIC} from 'common/constants'
import {ensureSelectedChild} from 'common/helpers/selectedChild'
import {FC, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {RootState, UserReducer} from 'setup'

// Only used by the hidden Onboarding Wizard switch (see below):
// import {getUserPreferences, updateUserPreferences} from 'setup/redux/Actions/Dashboard/dashboard'

import {ILayout, useLayout} from '../../core'
import BranchSwitcher from './BranchSwitcher'
import ChildSwitcher from './ChildSwitcher'
import SupportTicketsLink from './SupportTicketsLink'

/**
 * Desktop-only header strip: branch/child switcher, session badge and the
 * support link. Below `lg` the same controls live in HeaderUserMenu instead
 * (HeaderWrapper does not mount this there).
 */
const Header: FC = () => {
  const {config} = useLayout()

  const userReducer: UserReducer = useSelector<RootState>(
    ({userReducer}) => userReducer
  ) as UserReducer
  const user: UserModel = userReducer.user as UserModel

  /* Onboarding Wizard flip switch — HIDDEN: we are working on the new
     onboarding; until then this switch is hidden from all views (web +
     mobile). This is the state/handler for the commented-out switch in the
     JSX below — restore both together.

  const userPreferences = useSelector<RootState>(
    ({mainReducer}) => mainReducer.userPreferences
  ) as any

  const isOnboardingDisabled = userPreferences?.OnboardingWizard?.tutorial?.dashboard === false

  const [togglingOnboarding, setTogglingOnboarding] = useState(false)

  const handleToggleOnboarding = () => {
    setTogglingOnboarding(true)
    const newValue = !isOnboardingDisabled
    const body = {
      preferences: {
        ...userPreferences,
        OnboardingWizard: {
          ...(userPreferences?.OnboardingWizard ?? {}),
          tutorial: {
            ...(userPreferences?.OnboardingWizard?.tutorial ?? {}),
            dashboard: newValue ? false : true,
          },
        },
      },
    }
    dispatch(
      updateUserPreferences(
        body,
        () => {
          dispatch(
            getUserPreferences(
              () => {},
              () => {}
            )
          )
          setTogglingOnboarding(false)
        },
        () => setTogglingOnboarding(false)
      )
    )
  }
  */

  /* Only used by the hidden "Complete your onboarding" widget (see below):
  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as any
  */

  const activeSession = getActiveSession()

  useEffect(() => {
    if ([USER_TYPES_NUMERIC.PARENT].includes(user.type as number)) {
      ensureSelectedChild(user.children)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateDOM(config)
  }, [config])

  return (
    <div
      className='
        menu
        menu-rounded
        menu-column
        menu-lg-row
        my-5
        my-lg-0
        align-items-stretch
        fw-semibold
        px-2 px-lg-0
    '
      id='kt_app_header_menu'
      data-kt-menu='true'
    >
      {!isSelectiveAiDomains() && (
        <div className='d-flex align-items-center justify-content-center justify-content-lg-start flex-lg-fill flex-wrap'>
          {[USER_TYPES_NUMERIC.PARENT].includes(user.type as number) && (
            <ChildSwitcher list={user.children} />
          )}
          <BranchSwitcher className='w-150px w-xxl-150px' />
          {activeSession && activeSession?.db_postfix && (
            <div className='badge ms-1 p-4 fs-7' style={{backgroundColor: 'var(--bs-teal)'}}>
              Session: {activeSession.name}
            </div>
          )}

          {/* "Complete your onboarding" widget — HIDDEN: we are working on the
              new onboarding; until then this is hidden from all views
              (web + mobile). Restore by uncommenting this block and the
              ClientOnboardPage import at the top of the file.
          {hasPermission('user_manager_settings') && generalSettings.on_boarding_enabled === 1 && (
            <div className='ms-xl-auto'>
              <ClientOnboardPage />
            </div>
          )}
          */}

          {/* Onboarding Wizard flip switch — HIDDEN: we are working on the new
              onboarding; until then this switch is hidden from all views
              (web + mobile). Restore by uncommenting this block and the
              matching handler/state above (isOnboardingDisabled,
              togglingOnboarding, handleToggleOnboarding).
          {isLocalHost() && isAdmin() && (
            <div className='app-navbar-item ms-1 ms-md-4 d-flex align-items-center'>
              <label
                className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center gap-2 cursor-pointer mb-0'
                title='Toggle Onboarding Wizard'
              >
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={!isOnboardingDisabled}
                  onChange={handleToggleOnboarding}
                  disabled={togglingOnboarding}
                />
                <span className='form-check-label fw-semibold text-muted fs-7 d-none d-md-inline'>
                  Onboarding
                </span>
              </label>
            </div>
          )}
          */}

          <SupportTicketsLink />
        </div>
      )}

      {/* <MenuInner /> */}
    </div>
  )
}

const updateDOM = (config: ILayout) => {
  if (config.app?.header?.default?.fixed?.desktop) {
    document.body.setAttribute('data-kt-app-header-fixed', 'true')
  }

  if (config.app?.header?.default?.fixed?.mobile) {
    document.body.setAttribute('data-kt-app-header-fixed-mobile', 'true')
  }

  if (config.app?.header?.default?.stacked) {
    document.body.setAttribute('data-kt-app-header-stacked', 'true')
  }

  const appHeaderDefaultStickyEnabled = config.app?.header?.default?.sticky?.enabled
  let appHeaderDefaultStickyAttributes: {[attrName: string]: string} = {}
  if (appHeaderDefaultStickyEnabled) {
    appHeaderDefaultStickyAttributes = config.app?.header?.default?.sticky?.attributes as {
      [attrName: string]: string
    }
  }

  const appHeaderDefaultMinimizeEnabled = config.app?.header?.default?.minimize?.enabled
  let appHeaderDefaultMinimizeAttributes: {[attrName: string]: string} = {}
  if (appHeaderDefaultMinimizeEnabled) {
    appHeaderDefaultMinimizeAttributes = config.app?.header?.default?.minimize?.attributes as {
      [attrName: string]: string
    }
  }

  setTimeout(() => {
    const headerElement = document.getElementById('kt_app_header')
    // header
    if (headerElement) {
      if (appHeaderDefaultStickyEnabled) {
        for (const key in appHeaderDefaultStickyAttributes) {
          if (appHeaderDefaultStickyAttributes.hasOwnProperty(key)) {
            headerElement.setAttribute(key, appHeaderDefaultStickyAttributes[key])
          }
        }
      }

      if (appHeaderDefaultMinimizeEnabled) {
        for (const key in appHeaderDefaultMinimizeAttributes) {
          if (appHeaderDefaultMinimizeAttributes.hasOwnProperty(key)) {
            headerElement.setAttribute(key, appHeaderDefaultMinimizeAttributes[key])
          }
        }
      }
    }
  }, 0)
}

export {Header}
